import { createFileRoute, Link } from "@tanstack/react-router";
import { HiddenSeoContext } from "@/components/site/HiddenSeoContext";
import { motion } from "framer-motion";
import { lazy, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SiteShell } from "@/components/site/SiteShell";
import { SectionHeading } from "@/components/site/SectionHeading";
import { Button } from "@/components/ui/button";
import { howItWorks } from "@/lib/services";
import { SeoFaqBlock } from "@/components/site/SeoFaqBlock";
import { ServiceDetailSection } from "@/components/site/ServiceDetailSection";
import { SERVICE_DETAIL_CONTENT } from "@/lib/service-detail-content";
import { SEO_CONTENT } from "@/components/site/seo-content";
import { useWatermarkEnabled } from "@/hooks/use-watermark-settings";
import {
  ListChecks,
  CloudUpload,
  PackageCheck,
  Wallet,
  Send,
  ShieldCheck,
  PartyPopper,
  ArrowRight,
  Sparkles,
} from "lucide-react";

const HowWatermarks = lazy(() => import("@/components/site/HowWatermarks"));


import { webPageScript } from "@/lib/webpage-jsonld";

export const Route = createFileRoute("/how-it-works")({
  head: () => ({
    meta: [
      { title: "How Online Tax VAT Works | Step-by-Step Guide" },
      { name: "description", content: "Learn how to upload documents, choose a service, complete payment and receive tax or VAT filing support online in Bangladesh." },
      { property: "og:title", content: "How Online Tax VAT Works | Step-by-Step Guide" },
      { property: "og:description", content: "Learn our simple online workflow for tax, VAT and TIN services in Bangladesh." },
      { property: "og:url", content: "https://onlinetaxvat.com/how-it-works" },
    ],
    links: [{ rel: "canonical", href: "https://onlinetaxvat.com/how-it-works" }],
    scripts: [webPageScript({
      path: "/how-it-works",
      name: "How It Works — Online Tax VAT Bangladesh",
      description: "Learn how to upload documents, choose a service, complete payment and receive tax or VAT filing support online in Bangladesh.",
    })],
  }),
  component: HowPage,
});

const BN_STEPS: Record<string, { title: string; sub: string; desc: string }> = {
  "01": {
    title: "সার্ভিস সিলেক্ট করুন",
    sub: "Select Service",
    desc: "আমাদের এনবিআর (NBR) সম্পর্কিত সার্ভিসগুলোর পূর্ণ তালিকা ব্রাউজ করুন—যার মধ্যে রয়েছে আয়কর রিটার্ন (Income tax return), ভ্যাট রেজিস্ট্রেশন (BIN), মাসিক ভ্যাট রিটার্ন (Mushak 9.1), ই-টিন (e-TIN), ট্রেড লাইসেন্স, কোম্পানি ফাইলিং বা ভিসা ট্যাক্স ফাইল। প্রতিটি সার্ভিস পেজে নির্দিষ্ট স্কোপ, ডেলিভারির সময় এবং প্রয়োজনীয় কাগজপত্রের তালিকা স্পষ্টভাবে দেওয়া আছে। আপনার পরিস্থিতির জন্য কোন সার্ভিসটি প্রযোজ্য তা নিয়ে নিশ্চিত না থাকলে, মাত্র ২ মিনিটের ফ্রি পরামর্শের জন্য আমাদের হোয়াটসঅ্যাপে অ্যাডভাইজরের সাথে যোগাযোগ করুন।",
  },
  "02": {
    title: "ডকুমেন্টস আপলোড করুন",
    sub: "Upload Documents",
    desc: "আমাদের এনক্রিপ্টেড ড্যাশবোর্ডের মাধ্যমে নিরাপদে আপনার এনআইডি (NID), টিন সার্টিফিকেট (TIN certificate), বেতন বা ব্যবসার কাগজ, ব্যাংক স্টেটমেন্ট এবং পূর্ববর্তী রিটার্নগুলো আপলোড করুন। ফাইলগুলো (PDF/JPG/PNG ১০ মেগাবাইট পর্যন্ত) অ্যাক্সেস-নিয়ন্ত্রিত প্রাইভেট স্টোরেজে সংরক্ষিত থাকে এবং কেবল আপনার নির্দিষ্ট অ্যাসাইন করা অ্যাডভাইজর সেগুলো দেখতে পারেন। কোনো কাগজ অনুপস্থিত থাকলে তাৎক্ষণিকভাবে ক্লিয়ার চেকলিস্টের মাধ্যমে মার্ক করে দেওয়া হয়, যাতে আপনি সহজেই বুঝতে পারেন কী যুক্ত করতে হবে।",
  },
  "03": {
    title: "প্যাকেজ নির্বাচন করুন",
    sub: "Choose Package",
    desc: "আপনার প্রয়োজনের জরুরিতা ও জটিলতা অনুযায়ী প্যাকেজ বেছে নিন — সাধারণ রিটার্নের জন্য Basic, বেশিরভাগ ব্যক্তি ও এসএমই-এর জন্য Standard, এবং কর্পোরেট বা এক্সপ্রেস ভিসা কেসের জন্য Premium। প্রতিটি প্যাকেজের মূল্য BDT-তে নির্দিষ্ট থাকে, কোনো লুকানো চার্জ নেই এবং B2B / VIP ক্লায়েন্টরা স্বয়ংক্রিয়ভাবে চুক্তিভিত্তিক রেট দেখতে পান। কেস পরিবর্তন হলে ২৪ ঘণ্টার মধ্যে প্যাকেজ আপগ্রেড বা ডাউনগ্রেড করা যায়।",
  },
  "04": {
    title: "পেমেন্ট করুন",
    sub: "Make Payment",
    desc: "বিকাশ (bKash), নগদ (Nagad), রকেট (Rocket) অথবা সরাসরি ব্যাংক ট্রান্সফারের (Dutch-Bangla, BRAC, City, Eastern) মাধ্যমে খুব সহজেই পেমেন্ট করুন। চেকআউটের সময় প্যাকেজ মূল্য নির্দিষ্ট থাকে এবং ঐচ্ছিক ১% এনবিআর সরকারি চ্যানেল ফি আলাদাভাবে দেখানো হয়—এটি শুধুমাত্র আপনি যদি আমাদের মাধ্যমে আপনার নির্ধারিত কর পরিশোধ করতে বলেন। আমরা কখনই আপনার এমএফএস (MFS) পিন বা ওটিপি (OTP) সংরক্ষণ করি না।",
  },
  "05": {
    title: "ট্রানজেকশন সাবমিট করুন",
    sub: "Submit Transaction",
    desc: "আপনার ড্যাশবোর্ড থেকে ট্রানজেকশন আইডি (TrxID) এবং পেমেন্টের স্ক্রিনশট সাবমিট করুন। আমাদের অ্যাকাউন্ট টিম প্রতি কার্যদিবসে (শনি-বৃহস্পতি, সকাল ১০টা থেকে রাত ৮টা) ট্রানজেকশন রিকনসাইল বা যাচাই করে। একবার যাচাই হয়ে গেলে, অর্ডার স্ট্যাটাস \"In Review\"-এ পরিবর্তিত হয় এবং আপনার কেস অফিসার স্বয়ংক্রিয়ভাবে নোটিফিকেশন পেয়ে যান।",
  },
  "06": {
    title: "অ্যাডমিন রিভিউ",
    sub: "Admin Review",
    desc: "আমাদের এনবিআর-সার্টিফাইড বিশেষজ্ঞরা সর্বশেষ ফাইন্যান্স অ্যাক্ট, এসআরও (SROs) এবং সার্কুলারের আলোকে আপনার কাগজপত্র পর্যালোচনা করেন, রিটার্ন বা আবেদন প্রস্তুত করেন, প্রতিটি ফিগার ক্রস-চেক করেন এবং অফিসিয়াল এনবিআর ই-রিটার্ন, আইভাস (IVAS) বা সিটি কর্পোরেশন পোর্টালের মাধ্যমে তা ফাইলিং করেন। প্রসেসিং চলাকালীন এনবিআর থেকে যেকোনো প্রশ্ন বা জটিলতা আমরাই হ্যান্ডেল করি—আপনাকে কখনোই কোনো ট্যাক্স সার্কেল অফিসে যাওয়ার প্রয়োজন হয় না।",
  },
  "07": {
    title: "ডেলিভারি পান",
    sub: "Get Delivery",
    desc: "আপনার অফিশিয়াল অ্যাকনলেজমেন্ট স্লিপ, সার্টিফিকেট, ইনভয়েস এবং মুশাক ৬.৩ মানি রিসিট গ্রহণ করুন—সাধারণত ২৪ থেকে ৭২ ঘণ্টার মধ্যে WhatsApp, ইমেল এবং আপনার কাস্টমার ড্যাশবোর্ডের মাধ্যমে ডেলিভারি দেওয়া হয়। ভবিষ্যতের রেফারেন্স এবং নবায়নের জন্য প্রতিটি ফাইল আপনার অ্যাকাউন্টে সুরক্ষিত থাকে এবং প্রতিটি প্যাকেজের সাথে ৩০ দিনের ফ্রি পোস্ট-ফাইলিং সাপোর্ট অন্তর্ভুক্ত থাকে।",
  },
};

const stepStyles = [
  { Icon: ListChecks, gradient: "from-emerald-500 via-teal-500 to-emerald-600", glow: "shadow-[0_20px_60px_-20px_rgba(16,185,129,0.55)]", ring: "ring-emerald-400/40", chip: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300", iconAnim: "group-hover:rotate-[8deg]" },
  { Icon: CloudUpload, gradient: "from-sky-500 via-blue-500 to-indigo-600", glow: "shadow-[0_20px_60px_-20px_rgba(59,130,246,0.55)]", ring: "ring-sky-400/40", chip: "bg-sky-500/15 text-sky-600 dark:text-sky-300", iconAnim: "group-hover:-translate-y-1" },
  { Icon: PackageCheck, gradient: "from-fuchsia-500 via-purple-500 to-violet-600", glow: "shadow-[0_20px_60px_-20px_rgba(168,85,247,0.55)]", ring: "ring-fuchsia-400/40", chip: "bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-300", iconAnim: "group-hover:scale-110" },
  { Icon: Wallet, gradient: "from-amber-500 via-orange-500 to-rose-500", glow: "shadow-[0_20px_60px_-20px_rgba(245,158,11,0.6)]", ring: "ring-amber-400/40", chip: "bg-amber-500/15 text-amber-600 dark:text-amber-300", iconAnim: "group-hover:rotate-[-8deg]" },
  { Icon: Send, gradient: "from-cyan-500 via-teal-500 to-emerald-500", glow: "shadow-[0_20px_60px_-20px_rgba(20,184,166,0.55)]", ring: "ring-cyan-400/40", chip: "bg-cyan-500/15 text-cyan-600 dark:text-cyan-300", iconAnim: "group-hover:translate-x-1" },
  { Icon: ShieldCheck, gradient: "from-indigo-500 via-blue-600 to-sky-600", glow: "shadow-[0_20px_60px_-20px_rgba(99,102,241,0.55)]", ring: "ring-indigo-400/40", chip: "bg-indigo-500/15 text-indigo-600 dark:text-indigo-300", iconAnim: "group-hover:scale-110" },
  { Icon: PartyPopper, gradient: "from-rose-500 via-pink-500 to-fuchsia-600", glow: "shadow-[0_20px_60px_-20px_rgba(244,63,94,0.55)]", ring: "ring-rose-400/40", chip: "bg-rose-500/15 text-rose-600 dark:text-rose-300", iconAnim: "group-hover:rotate-[12deg]" },
];

function HowPage() {
  const { t, i18n } = useTranslation();
  const showWatermark = useWatermarkEnabled("how_it_works");
  const [deferReady, setDeferReady] = useState(false);
  useEffect(() => {
    const w = window as any;
    const cb = () => setDeferReady(true);
    if (typeof w.requestIdleCallback === "function") w.requestIdleCallback(cb, { timeout: 2500 });
    else setTimeout(cb, 1500);
  }, []);
  return (
    <SiteShell>
      <section className="relative pt-14 md:pt-20 pb-20 overflow-hidden" style={{ contain: "layout style" }}>
        {showWatermark && deferReady && (
          <Suspense fallback={null}>
            <HowWatermarks />
          </Suspense>
        )}
        <div className="container-page relative z-10">
          <h1 className="sr-only">How Online Tax & VAT Filing Works in Bangladesh — Step-by-Step Process</h1>
          <SectionHeading
            eyebrow={t("howItWorksPage.eyebrow")}
            title={<>{t("howItWorksPage.titleA")}<span className="gradient-text">{t("howItWorksPage.titleB")}</span></>}
            description={t("howItWorksPage.desc")}
          />


          <div className="mt-14 relative">
            {/* central timeline line */}
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-primary/40 via-primary/20 to-transparent md:-translate-x-px" />
            <div className="space-y-8 md:space-y-10">
              {howItWorks.map((s, i) => {
                const style = stepStyles[i] ?? stepStyles[0];
                const Icon = style.Icon;
                const isRight = i % 2 === 1;
                const isBn = (i18n.language || "").toLowerCase().startsWith("bn");
                const bnStep = BN_STEPS[s.step];
                const titleText = isBn && bnStep ? bnStep.title : s.title;
                const bnLabel = isBn && bnStep ? bnStep.sub : s.bn;
                const descText = isBn && bnStep ? bnStep.desc : s.desc;
                return (
                  <motion.div
                    key={s.step}
                    initial={{ opacity: 0, y: 22 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ delay: i * 0.06, duration: 0.5, ease: "easeOut" }}
                    className={`relative md:grid md:grid-cols-2 md:gap-12 ${isRight ? "md:[&>*:first-child]:col-start-2" : ""}`}
                    style={i > 1 ? { contentVisibility: "auto", containIntrinsicSize: "360px 320px" } as any : undefined}
                  >

                    <div className={`group pl-16 md:pl-0 ${isRight ? "md:pl-12" : "md:pr-12"}`}>
                      <div className="relative">
                        {/* outer gradient glow border */}
                        <div className={`absolute -inset-px rounded-3xl bg-gradient-to-br ${style.gradient} opacity-60 blur-[2px] group-hover:opacity-100 transition-opacity duration-500`} />
                        <div className={`relative rounded-3xl bg-card/95 backdrop-blur-xl border border-white/40 dark:border-white/10 p-6 md:p-7 ${style.glow} transition-all duration-500 group-hover:-translate-y-1.5 overflow-hidden`}>
                          {/* decorative corner gradient */}
                          <div className={`pointer-events-none absolute -top-16 -right-16 h-44 w-44 rounded-full bg-gradient-to-br ${style.gradient} opacity-20 blur-3xl`} />
                          {/* big step number watermark - top right */}
                          <span aria-hidden className="pointer-events-none absolute top-2 right-4 z-0 font-display font-extrabold text-5xl md:text-6xl tracking-wider text-emerald-500/15 dark:text-white/10 select-none">
                            {s.step}
                          </span>

                          {/* top row: step chip + sparkle */}
                          <div className="relative flex items-center justify-between">
                            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${style.chip}`}>
                              <Sparkles className="h-3 w-3 animate-pulse" />
                              {isBn ? "স্টেপ" : "STEP"} {s.step}
                            </span>
                            <span className={`h-2 w-2 rounded-full bg-gradient-to-br ${style.gradient} animate-pulse`} />
                          </div>

                          {/* live animated icon badge */}
                          <div className="relative mt-5">
                            <div className={`relative inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br ${style.gradient} ${style.glow} ring-2 ${style.ring} transition-transform duration-500 ${style.iconAnim}`}>
                              <span className={`absolute inset-0 rounded-2xl ring-2 ${style.ring} animate-ping opacity-40`} />
                              <Icon className="relative h-8 w-8 text-white drop-shadow" strokeWidth={2.2} />
                            </div>
                          </div>

                          {/* title + bn + desc */}
                          <h3 className="relative mt-5 font-display font-bold text-xl tracking-tight">
                            {titleText}
                          </h3>
                          <p className={`relative font-bn font-semibold text-sm mt-1 bg-gradient-to-r ${style.gradient} bg-clip-text text-transparent`}>
                            {bnLabel}
                          </p>
                          <p className="relative mt-3 text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                            {descText}
                          </p>


                          {/* bottom accent line */}
                          <div className={`relative mt-5 h-[3px] w-12 rounded-full bg-gradient-to-r ${style.gradient} group-hover:w-24 transition-all duration-500`} />
                        </div>
                      </div>
                    </div>
                    {/* timeline dot */}
                    <div className={`absolute left-6 md:left-1/2 top-8 -translate-x-1/2 h-5 w-5 rounded-full bg-gradient-to-br ${style.gradient} ${style.glow} ring-4 ring-background`} />
                  </motion.div>
                );
              })}
            </div>
          </div>


          <div className="mt-16 text-center">
            <Button asChild size="lg" className="gradient-primary text-primary-foreground shadow-glow">
              <Link to="/services">{t("howItWorksPage.cta")} <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
            </Button>
          </div>
          {(() => {
            const c = SEO_CONTENT["how-it-works"];
            const isBn = (i18n.language || "").toLowerCase().startsWith("bn");
            const bn = {
              title: "আমাদের ৭-ধাপের প্রক্রিয়া — দ্রুততা এবং নির্ভুলতার জন্য ডিজাইন করা",
              intro: "অনলাইনে ট্যাক্স বা ভ্যাট দাখিল করা এখন খাবার অর্ডার করার মতোই সহজ। কাস্টমারদের মূল্যবান সময় বাঁচাতে এবং শতভাগ নির্ভুলতা নিশ্চিত করতে আমরা প্যাকেজ সিলেকশন থেকে শুরু করে এনবিআর প্রাপ্তি স্বীকারপত্র পর্যন্ত প্রতিটি ধাপকে অত্যন্ত সহজ ও সুবিন্যস্ত করেছি।",
              bullets: [
                "ধাপ ১ — আমাদের ক্যাটালগ থেকে সঠিক সেবাটি বেছে নিন",
                "ধাপ ২ — আপনার ডকুমেন্ট আপলোড করুন (১০ এমবি পর্যন্ত PDF, JPG, PNG)",
                "ধাপ ৩ — আপনার প্রয়োজন অনুযায়ী সঠিক প্যাকেজটি সিলেক্ট করুন",
                "ধাপ ৪ — বিকাশ, নগদ, রকেট বা ব্যাংক ট্রান্সফারের মাধ্যমে পেমেন্ট করুন",
                "ধাপ ৫ — ভেরিফিকেশনের জন্য আপনার ট্রানজেকশন আইডি (Transaction ID) সাবমিট করুন",
                "ধাপ ৬ — আমাদের প্রত্যয়িত উপদেষ্টা আপনার রিটার্ন প্রস্তুত এবং দাখিল করবেন",
                "ধাপ ৭ — আপনার ড্যাশবোর্ডে অফিশিয়াল প্রাপ্তি স্বীকারপত্র ও ইনভয়েস বুঝে নিন",
              ],
              faqs: [
                { q: "সম্পূর্ণ প্রক্রিয়াটি সম্পন্ন হতে কত সময় লাগে?", a: "আপনার প্রয়োজনীয় সকল সঠিক ডকুমেন্ট সাবমিট করার পর সাধারণত ১ থেকে ৩ কর্মদিবসের মধ্যে আমাদের এক্সপার্ট টিম রিটার্ন প্রস্তুত করে অফিশিয়াল পোর্টালে দাখিল সম্পন্ন করে দেয়।" },
                { q: "আমার কোনো ডকুমেন্ট কম থাকলে বা খুঁজে না পেলে কী হবে?", a: "কোনো দুশ্চিন্তা করবেন না! আপনার জন্য নির্ধারিত কেস অফিসার হোয়াটসঅ্যাপ বা ড্যাশবোর্ডে আপনাকে গাইড করবেন এবং প্রয়োজনীয় বিকল্প ডকুমেন্ট বা তথ্য দিয়ে কীভাবে ফাইল রেডি করা যায় তা বুঝিয়ে দেবেন।" },
                { q: "আমি কি আমার ফাইলের অগ্রগতি ট্র্যাক করতে পারব?", a: "হ্যাঁ, অবশ্যই! আপনার কাস্টমার ড্যাশবোর্ডে লগইন করলেই রিয়েল-টাইমে দেখতে পাবেন আপনার ফাইলটি বর্তমানে কোন ধাপে আছে (যেমন: রিভিউ চলছে, পেমেন্ট ভেরিফাইড কিংবা ফাইল সাবমিটেড)।" },
                { q: "পরবর্তীতে এনবিআর (NBR) কোনো প্রশ্ন বা নোটিশ পাঠালে কী হবে?", a: "আমাদের প্রতিটি সেবার সাথে ৩০ দিনের ফ্রি পোস্ট-ফাইলিং সাপোর্ট অন্তর্ভুক্ত থাকে। এই সময়ের মধ্যে এনবিআর কোনো নোটিশ বা সংশোধনের অনুরোধ পাঠালে আমাদের প্রত্যয়িত উপদেষ্টারা সম্পূর্ণ ফ্রিতে তা সমাধান করে দেবেন।" },
              ],
            };
            const data = isBn ? bn : c;
            return <SeoFaqBlock title={data.title} intro={data.intro} bullets={data.bullets} faqs={data.faqs} />;
          })()}
          <ServiceDetailSection content={SERVICE_DETAIL_CONTENT["how-it-works"]} />
        </div>
      </section>
      <HiddenSeoContext topic="howItWorks" />
    </SiteShell>
  );
}
